/**
 * The task pop-ups, worded exactly as the prototype's (docs/prototype.html askProof, pushModal, blockModal,
 * reassignModal, askChange, whyModal/dropTaskModal, chgDecide, newTaskModal). Each one makes ONE contract call
 * (routes.step2) and leaves the server to decide: who may do it, the slip count, the flag at 3 slips, the
 * proof rules (MIS tasks need a next step) and the audit row with both people when acting as someone.
 * Add a task with a Repeat makes a routine instead (routes.step7 POST /clients/{id}/routines).
 *
 * Writes carry the task's `version` as If-Match and ONE Idempotency-Key per open pop-up (made when it opens, not
 * per attempt), so a double click, or a retry after a lost response, replays the first result instead of applying
 * the change twice. The server keeps a key only for a write that succeeded, so retrying after a 422/412 is fine.
 */
import { useState } from 'react';
import {
  CHANGE_REASON_LABEL,
  DROP_REASON_LABEL,
  ROUTINE_EVERY,
  ROUTINE_EVERY_LABEL,
  RoutineDayOfMonth,
  SLIP_REASON_LABEL,
  isMisTask,
  type ChangeDecision,
  type ChangeReason,
  type ChangeRequest,
  type CreateRoutineBody,
  type DropReason,
  type RoutineEvery,
  type SlipReason,
  type Task,
  type TaskPriority,
} from '@amplispot/contracts';
import { ApiFailure, call, newIdempotencyKey } from '../../api';
import { useCreateRoutine } from '../../lib/checklists';
import { useClients, useUsers } from '../../lib/data';
import { dayLabel, firstName, todayIso } from '../../lib/format';
import { useSession } from '../../lib/session';
import { addDays, useWorkWrite } from '../../lib/work';
import { Field, Modal, Seg, errorText, useFail, useToast } from '../../ui/kit';

type Close = () => void;

const opts = <K extends string>(labels: Record<K, string>) => Object.entries(labels) as [K, string][];

/** People a task can go to: active founder, Ops associate director and Ops members (prototype OPS). */
function useOpsPeople(enabled: boolean) {
  const users = useUsers(enabled);
  const people = (users.data ?? []).filter((u) => u.status === 'active' && (u.role === 'ops_ad' || u.role === 'ops_member' || u.role === 'founder'));
  return { people, isLoading: users.isLoading };
}

// ---------------------------------------------------------------- close with proof

export function CloseWithProofModal({ task, onClose, onDone }: { task: Task; onClose: Close; onDone?: () => void }) {
  const { errs, fail, toast } = useFail(['proof_url', 'proof_text', 'next_step']);
  const [link, setLink] = useState('');
  const [none, setNone] = useState(false);
  const [note, setNote] = useState('');
  const [next, setNext] = useState('');
  const [local, setLocal] = useState('');
  const mis = isMisTask(task.title);
  const [key] = useState(newIdempotencyKey);
  const proofLink = none ? '' : link.trim();
  const save = useWorkWrite(() =>
    call('tasks.close', {
      params: { id: task.id },
      ifMatch: task.version,
      idempotencyKey: key,
      body: proofLink
        ? { proof_kind: 'link', proof_url: proofLink, proof_text: note.trim() || undefined, next_step: mis ? next.trim() : undefined }
        : { proof_kind: 'note', proof_text: note.trim(), no_attachment_needed: true, next_step: mis ? next.trim() : undefined },
    }),
  );
  const submit = () => {
    if (!link.trim() && !none) return setLocal('Paste a link, or tick No attachment needed');
    if (!note.trim()) return setLocal('Say in a line what was done');
    if (mis && next.trim().length < 3) return setLocal('Say what you suggest next. A report on its own is a tick-mark.');
    setLocal('');
    save.mutate(undefined, {
      onSuccess: () => {
        toast('Closed with proof');
        onDone?.();
        onClose();
      },
      onError: fail,
    });
  };
  return (
    <Modal
      title="Show it is done"
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Not yet
          </button>
          <button className="btn good" onClick={submit} disabled={save.isPending} aria-busy={save.isPending}>
            {save.isPending ? 'Closing…' : 'Close with proof'}
          </button>
        </>
      }
    >
      <p style={{ marginTop: 0 }}>
        Before this task closes, attach something that proves it: the email you sent, the report, or a link. The Ops associate director checks it.
      </p>
      <p className="sub" style={{ marginTop: -6 }}>
        <b>{task.title}</b>
      </p>
      <Field label="Paste a link" error={errs.proof_url}>
        <input className="in" value={link} disabled={none} onChange={(e) => setLink(e.target.value)} placeholder="e.g. link to the email thread or shared folder" inputMode="url" />
      </Field>
      <label className="split" style={{ margin: '2px 0 10px', alignItems: 'flex-start' }}>
        <input type="checkbox" checked={none} onChange={(e) => setNone(e.target.checked)} style={{ marginTop: 4 }} />
        <span style={{ fontSize: 13 }}>
          <b>No attachment needed for this task.</b>{' '}
          <span className="sub">Close it with a one-line note instead, for example when the work was a call or a quick check.</span>
        </span>
      </label>
      <Field label="What was done" error={errs.proof_text}>
        <textarea className="in" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="One line is enough" />
      </Field>
      {mis ? (
        <Field label="What do you suggest next?" error={errs.next_step} hint="An MIS needs a next step. A report on its own is a tick-mark.">
          <textarea className="in" rows={2} value={next} onChange={(e) => setNext(e.target.value)} placeholder="e.g. Move the 3 lowest-response branches to the Hindi script" />
        </Field>
      ) : null}
      {local ? <div className="ferr" role="alert">{local}</div> : null}
    </Modal>
  );
}

// ---------------------------------------------------------------- move the due date

export function MoveDueModal({ task, onClose }: { task: Task; onClose: Close }) {
  const { errs, fail, toast } = useFail(['due', 'reason_code', 'note']);
  const today = todayIso();
  const base = task.due > today ? task.due : today;
  const choices = ([[1, 'Tomorrow'], [3, 'In 3 days'], [7, 'Next week'], [14, 'In 2 weeks']] as const)
    .map(([n, l]) => [addDays(today, n), l] as const)
    .filter(([d]) => d > base);
  const [due, setDue] = useState<string>(choices[0]?.[0] ?? addDays(base, 1));
  const [reason, setReason] = useState<SlipReason>('waiting_on_client');
  const [note, setNote] = useState('');
  const [key] = useState(newIdempotencyKey);
  const save = useWorkWrite(() =>
    call('tasks.moveDue', {
      params: { id: task.id },
      ifMatch: task.version,
      idempotencyKey: key,
      body: { due, reason_code: reason, note: note.trim() || undefined },
    }),
  );
  const submit = () =>
    save.mutate(undefined, {
      onSuccess: (t) => {
        toast(t.flagged && t.slip_count >= 3 ? 'Date moved. Pushed 3 times, flagged to the Ops associate director.' : 'Date moved and recorded');
        onClose();
      },
      onError: fail,
    });
  return (
    <Modal
      title="Move the due date"
      sub={
        <>
          {task.title}
          <br />
          <span className="sub">
            Due {dayLabel(task.due)}
            {task.orig_due && task.orig_due !== task.due ? `, first due ${dayLabel(task.orig_due)}` : ''}. Every move is recorded with its reason.
          </span>
        </>
      }
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={submit} disabled={save.isPending} aria-busy={save.isPending}>
            Move date
          </button>
        </>
      }
    >
      <Field label="New due date" error={errs.due}>
        <div className="split" style={{ gap: 8 }}>
          {choices.length ? (
            <select className="in" value={choices.some(([d]) => d === due) ? due : ''} onChange={(e) => e.target.value && setDue(e.target.value)}>
              {choices.map(([d, l]) => (
                <option key={d} value={d}>
                  {l}, {dayLabel(d)}
                </option>
              ))}
              {!choices.some(([d]) => d === due) ? <option value="">Another date</option> : null}
            </select>
          ) : null}
          <input className="in" type="date" value={due} min={today} onChange={(e) => e.target.value && setDue(e.target.value)} style={{ width: 'auto' }} aria-label="Pick a date" />
        </div>
      </Field>
      <Field label="Why is it moving?" error={errs.reason_code}>
        <select className="in" value={reason} onChange={(e) => setReason(e.target.value as SlipReason)}>
          {opts(SLIP_REASON_LABEL).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Add a note (required if Other)" error={errs.note}>
        <input className="in" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Client confirmed data on Thursday" />
      </Field>
      {task.slip_count >= 2 ? (
        <div className="note" style={{ margin: 0 }}>
          This is move {task.slip_count + 1}. At 3 moves the task is flagged to the Ops associate director.
        </div>
      ) : null}
    </Modal>
  );
}

// ---------------------------------------------------------------- blocked on someone

export function BlockModal({ task, onClose }: { task: Task; onClose: Close }) {
  const { errs, fail, toast } = useFail(['blocked_on']);
  const [on, setOn] = useState(task.blocked?.on ?? '');
  const [key] = useState(newIdempotencyKey);
  const save = useWorkWrite((clear: boolean) =>
    clear
      ? call('tasks.unblock', { params: { id: task.id }, ifMatch: task.version })
      : call('tasks.block', { params: { id: task.id }, ifMatch: task.version, idempotencyKey: key, body: { blocked_on: on.trim() } }),
  );
  const go = (clear: boolean) =>
    save.mutate(clear || !on.trim(), {
      onSuccess: () => {
        toast(clear || !on.trim() ? 'Blocker cleared' : 'Marked as blocked');
        onClose();
      },
      onError: fail,
    });
  return (
    <Modal
      title="What is this waiting on?"
      sub="Name the person or thing. It shows in the Blocked on view and the daily digest."
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          {task.blocked ? (
            <button className="btn" onClick={() => go(true)} disabled={save.isPending}>
              Clear blocker
            </button>
          ) : null}
          <button className="btn pri" onClick={() => go(false)} disabled={save.isPending || (!task.blocked && on.trim().length < 2)}>
            Save
          </button>
        </>
      }
    >
      <Field label="Waiting on" error={errs.blocked_on}>
        <input className="in" value={on} onChange={(e) => setOn(e.target.value)} placeholder="e.g. Client data team" autoFocus />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------- change who does this

export function ReassignModal({ task, onClose }: { task: Task; onClose: Close }) {
  const { errs, fail, toast } = useFail(['user_id', 'reason']);
  const { people } = useOpsPeople(true);
  const spoc = task.client?.spoc ?? null;
  const [who, setWho] = useState(task.assignee?.id ?? '');
  const [reason, setReason] = useState('');
  const [key] = useState(newIdempotencyKey);
  // The current assignee may not be in the active Ops list (disabled, or another role): show them as the
  // selected, unpickable first option so the select never displays someone `who` is not.
  const unlisted = task.assignee && !people.some((p) => p.id === task.assignee!.id) ? task.assignee : null;
  const save = useWorkWrite(() =>
    call('tasks.reassign', { params: { id: task.id }, ifMatch: task.version, idempotencyKey: key, body: { user_id: who, reason: reason.trim() } }),
  );
  const submit = () =>
    save.mutate(undefined, {
      onSuccess: (t) => {
        toast(`Now with ${t.assignee ? firstName(t.assignee.name) : 'nobody'}`);
        onClose();
      },
      onError: fail,
    });
  return (
    <Modal
      title="Change who does this"
      sub={
        <>
          {task.title}
          <br />
          <span className="sub">
            {task.client && spoc ? `${task.client.name} is looked after by ${spoc.name}.` : 'Internal task.'} Now with {task.assignee?.name ?? 'nobody'}.
          </span>
        </>
      }
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={submit} disabled={save.isPending || !who || who === task.assignee?.id}>
            Change
          </button>
        </>
      }
    >
      <Field label="Give it to" error={errs.user_id}>
        <select className="in" value={who} onChange={(e) => setWho(e.target.value)}>
          {!task.assignee ? <option value="">Pick a person</option> : null}
          {unlisted ? (
            <option value={unlisted.id} disabled>
              {unlisted.name} (now)
            </option>
          ) : null}
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.id === spoc?.id ? ' (client SPOC)' : ''}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Reason" error={errs.reason}>
        <input className="in" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. They are on leave this week" />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------- ask for a change (members)

export function AskChangeModal({ task, onClose }: { task: Task; onClose: Close }) {
  const { can, me } = useSession();
  const { errs, fail, toast } = useFail(['reason_code', 'suggested_user_id', 'note']);
  // Members cannot list people (users.read), so the suggested person only shows for those who can.
  const { people } = useOpsPeople(can('users.read'));
  const [reason, setReason] = useState<ChangeReason>('someone_else_doing_it');
  const [who, setWho] = useState('');
  const [note, setNote] = useState('');
  const [key] = useState(newIdempotencyKey);
  const save = useWorkWrite(() =>
    call('changeRequests.raise', {
      params: { id: task.id },
      idempotencyKey: key,
      body: { reason_code: reason, suggested_user_id: who || undefined, note: note.trim() || undefined },
    }),
  );
  const submit = () =>
    save.mutate(undefined, {
      onSuccess: () => {
        toast('Sent to the Ops associate director');
        onClose();
      },
      onError: fail,
    });
  return (
    <Modal
      title="Ask for a change"
      sub={
        <>
          {task.title}
          <br />
          <span className="sub">You cannot reassign it yourself. The Ops associate director decides.</span>
        </>
      }
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={submit} disabled={save.isPending}>
            Send to Ops associate director
          </button>
        </>
      }
    >
      <Field label="What is the problem?" error={errs.reason_code}>
        <select className="in" value={reason} onChange={(e) => setReason(e.target.value as ChangeReason)}>
          {opts(CHANGE_REASON_LABEL).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </Field>
      {people.length ? (
        <Field label="Who is doing it, or who should? (optional)" error={errs.suggested_user_id}>
          <select className="in" value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="">Not sure</option>
            {people
              .filter((p) => p.id !== me.user.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </Field>
      ) : null}
      <Field label="Anything the Ops associate director should know" error={errs.note}>
        <textarea className="in" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. A teammate sent this to the client on Friday" />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------- drop this task (whyModal)

export function DropTaskModal({ task, onClose, onDone }: { task: Task; onClose: Close; onDone?: () => void }) {
  const { errs, fail, toast } = useFail(['reason_code', 'note']);
  const [reason, setReason] = useState<DropReason>('duplicate');
  const [note, setNote] = useState('');
  const [key] = useState(newIdempotencyKey);
  const save = useWorkWrite(() =>
    call('tasks.drop', { params: { id: task.id }, ifMatch: task.version, idempotencyKey: key, body: { reason_code: reason, note: note.trim() || undefined } }),
  );
  const submit = () =>
    save.mutate(undefined, {
      onSuccess: () => {
        toast('Task dropped and logged.');
        onDone?.();
        onClose();
      },
      onError: fail,
    });
  return (
    <Modal
      title="Drop this task"
      sub={
        <>
          “{task.title}”
          <br />
          The Ops associate director sees what was left out and why.
        </>
      }
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn bad" onClick={submit} disabled={save.isPending}>
            Leave it out
          </button>
        </>
      }
    >
      <Field label="Why" error={errs.reason_code}>
        <select className="in" value={reason} onChange={(e) => setReason(e.target.value as DropReason)}>
          {opts(DROP_REASON_LABEL).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Note (needed if Other)" error={errs.note}>
        <input className="in" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------- reopen

export function ReopenModal({ task, onClose }: { task: Task; onClose: Close }) {
  const { errs, fail, toast } = useFail(['reason']);
  const [reason, setReason] = useState('');
  const [key] = useState(newIdempotencyKey);
  const save = useWorkWrite(() =>
    call('tasks.reopen', { params: { id: task.id }, ifMatch: task.version, idempotencyKey: key, body: { reason: reason.trim() } }),
  );
  return (
    <Modal
      title="Reopen this task"
      sub={task.title}
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn pri"
            disabled={save.isPending || reason.trim().length < 3}
            onClick={() =>
              save.mutate(undefined, {
                onSuccess: () => {
                  toast('Reopened');
                  onClose();
                },
                onError: fail,
              })
            }
          >
            Reopen
          </button>
        </>
      }
    >
      <Field label="Why is it reopening?" error={errs.reason}>
        <input className="in" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. The client found a mistake in the sheet" autoFocus />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------- decide a change request (AD)

export function DecideChangeModal({ cr, onClose }: { cr: ChangeRequest; onClose: Close }) {
  const { errs, fail, toast } = useFail(['user_id', 'note']);
  const { people } = useOpsPeople(true);
  const by = firstName(cr.by.name);
  const [choice, setChoice] = useState<ChangeDecision>('reassign');
  const [who, setWho] = useState(cr.suggested_user?.id ?? '');
  const [note, setNote] = useState('');
  const [key] = useState(newIdempotencyKey);
  const save = useWorkWrite(() =>
    call('changeRequests.decide', {
      params: { id: cr.id },
      idempotencyKey: key,
      body: { decision: choice, user_id: choice === 'reassign' ? who : undefined, note: note.trim() || undefined },
    }),
  );
  const submit = () =>
    save.mutate(undefined, {
      onSuccess: (r) => {
        toast(choice === 'keep' ? `Kept with ${by}` : choice === 'remove' ? 'Task removed as a duplicate' : `Now with ${r.task?.assignee ? firstName(r.task.assignee.name) : 'them'}`);
        onClose();
      },
      onError: fail,
    });
  return (
    <Modal
      title={`${by} asks for a change`}
      sub={
        <>
          <b>{cr.task_title}</b>
          {cr.client ? (
            <>
              <br />
              {cr.client.name}
            </>
          ) : null}
        </>
      }
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Later
          </button>
          <button className="btn pri" onClick={submit} disabled={save.isPending || (choice === 'reassign' && !who)}>
            Decide
          </button>
        </>
      }
    >
      <div className="note" style={{ margin: '0 0 14px' }}>
        <b>{CHANGE_REASON_LABEL[cr.reason_code]}</b>
        {cr.suggested_user ? (
          <>
            <br />
            Suggests: {cr.suggested_user.name}
          </>
        ) : null}
        {cr.note ? (
          <>
            <br />“{cr.note}”
          </>
        ) : null}
      </div>
      <div className="popts" style={{ gridTemplateColumns: '1fr' }}>
        {(
          [
            ['reassign', 'Give it to someone else'],
            ['remove', 'Remove it, it is a duplicate'],
            ['keep', `Keep it with ${by}`],
          ] as const
        ).map(([k, l]) => (
          <button key={k} type="button" className={`popt${choice === k ? ' on' : ''}`} onClick={() => setChoice(k)} aria-pressed={choice === k}>
            <b>{l}</b>
          </button>
        ))}
      </div>
      {choice === 'reassign' ? (
        <Field label="Give it to" error={errs.user_id}>
          <select className="in" value={who} onChange={(e) => setWho(e.target.value)} style={{ marginTop: 14 }}>
            <option value="">Pick a person</option>
            {people
              .filter((p) => p.id !== cr.by.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </Field>
      ) : null}
      <Field label={`Note back to ${by} (required when you keep it)`} error={errs.note}>
        <input className="in" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Please finish it, the other SPOC is on leave" />
      </Field>
    </Modal>
  );
}

// ---------------------------------------------------------------- add a task

/** The next 14 days the date picker offers (prototype ntDates). */
const PICK_DAYS = 14;
const isWeekend = (iso: string) => {
  const d = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return d === 0 || d === 6;
};
const shortDay = (iso: string, today: string) => (iso === today ? 'Today' : iso === addDays(today, 1) ? 'Tomorrow' : dayLabel(iso).replace(/ \w+$/, ''));
/** Prototype WEEKDAYS: a weekly or every-2-weeks routine falls on a working day. */
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const;
type Weekday = (typeof WEEKDAYS)[number];
type Repeat = 'none' | RoutineEvery;

export function AddTaskModal({ onClose, onCreated, clientId }: { onClose: Close; onCreated?: (t: Task) => void; clientId?: string }) {
  const { can, me } = useSession();
  const { errs, setErrs, fail, toast } = useFail(['title', 'client_id', 'assignee_id', 'priority', 'dates', 'due', 'what', 'every', 'weekday', 'day_of_month', 'start_phase']);
  const clients = useClients();
  const pick = can('task.reassign');
  const { people } = useOpsPeople(pick);
  const today = todayIso();
  const [title, setTitle] = useState('');
  const [client, setClient] = useState(clientId ?? '');
  const [who, setWho] = useState(me.user.id);
  const [prio, setPrio] = useState<TaskPriority>('medium');
  // Prototype ntR / everyPicker: "Does not repeat", or a routine for the client's SPOC (POST /clients/{id}/routines).
  // Setting up routines is checklist.write.any (any client) or checklist.write.own (a client you are the SPOC of).
  const anyRoutine = can('checklist.write.any');
  const canRepeat = anyRoutine || can('checklist.write.own');
  const [repeat, setRepeat] = useState<Repeat>('none');
  const [weekday, setWeekday] = useState<Weekday>('Mon');
  const [dom, setDom] = useState('15');
  const [start, setStart] = useState<'this' | 'next'>('this');
  const repeating = repeat !== 'none';
  // Prototype ntDates: tick one date, or several (one task each, the server titles them "{title} ({day})").
  const days = Array.from({ length: PICK_DAYS }, (_, i) => addDays(today, i));
  const [dates, setDates] = useState<string[]>([addDays(today, 1)]);
  const toggle = (d: string) => setDates((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort()));
  const quick = (k: 'week' | 'next3' | 'none') => {
    if (k === 'none') return setDates([]);
    if (k === 'next3') return setDates(days.slice(1).filter((d) => !isWeekend(d)).slice(0, 3));
    // The rest of this week: today up to Friday, working days only (prototype ntPick('week')).
    const out: string[] = [];
    for (const d of days) {
      if (isWeekend(d)) {
        if (out.length) break;
        continue;
      }
      out.push(d);
    }
    setDates(out);
  };
  const [key] = useState(newIdempotencyKey);
  const [routineKey] = useState(newIdempotencyKey);
  const c = clients.data?.find((x) => x.id === client) ?? null;
  // Anyone but the AD gets the default: the client's SPOC, or themselves for internal work.
  const defaultWho = c ? c.spoc : me.user;
  const save = useWorkWrite(() =>
    call('tasks.create', {
      idempotencyKey: key,
      body: {
        title: title.trim(),
        client_id: client || undefined,
        assignee_id: pick ? who : undefined,
        ...(dates.length > 1 ? { dates } : { due: dates[0] }),
        priority: prio,
        source: 'adhoc',
      },
    }),
  );
  const routine = useCreateRoutine();
  // The day as typed, checked by the contract's own rule (the server's 422 says the same): never clamp
  // or default it, so a 31 or an empty box is refused with "Pick a day from 1 to 28", not saved as another day.
  const day = RoutineDayOfMonth.safeParse(dom.trim() === '' ? 0 : Number(dom));
  const routineBody = (every: RoutineEvery, dayOfMonth: number | undefined): CreateRoutineBody => {
    const body: CreateRoutineBody = { what: title.trim(), every };
    if (every === 'month') body.day_of_month = dayOfMonth;
    else if (every !== 'day') {
      body.weekday = weekday;
      if (every === 'fortnight') body.start_phase = start;
    }
    return body;
  };
  const saveRoutine = (every: RoutineEvery) => {
    // Prototype saveNewTask: a task that repeats belongs to a client and goes to that client's SPOC.
    if (!c) return setErrs({ client_id: 'Pick a client for a task that repeats' });
    if (!anyRoutine && c.spoc.id !== me.user.id) {
      return setErrs({ client_id: `Only ${firstName(c.spoc.name)}, the SPOC, or a manager can make a task repeat for ${c.name}` });
    }
    if (every === 'month' && !day.success) return setErrs({ day_of_month: day.error.issues[0]!.message });
    routine.mutate(
      { clientId: c.id, body: routineBody(every, day.data), key: routineKey },
      {
        onSuccess: () => {
          toast(`Added to ${c.name}. Repeats ${ROUTINE_EVERY_LABEL[every].toLowerCase()} for ${firstName(c.spoc.name)}.`);
          onClose();
        },
        onError: (e) => {
          fail(e);
          // The routine's "what" is this form's "What needs doing"; a duplicate (409) also names "what".
          const what = e instanceof ApiFailure ? e.fieldErrors.what : undefined;
          if (what) setErrs((cur) => ({ ...cur, title: what }));
        },
      },
    );
  };
  const busy = save.isPending || routine.isPending;
  const submit = () => {
    if (repeat !== 'none') return saveRoutine(repeat);
    save.mutate(undefined, {
      onSuccess: (r) => {
        const t = r.data[0];
        const forWhom = t?.assignee && t.assignee.id !== me.user.id ? ` for ${firstName(t.assignee.name)}` : '';
        toast(r.data.length > 1 ? `${r.data.length} tasks added${forWhom}, one for each date` : `Task added${forWhom}`);
        if (t) onCreated?.(t);
        onClose();
      },
      onError: fail,
    });
  };
  return (
    <Modal
      title="Add a task"
      sub={`For work that did not come from a note. A client task goes to that client’s SPOC.${canRepeat ? ' It can repeat.' : ''}`}
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={submit} disabled={busy || title.trim().length < 3 || (!repeating && !dates.length)}>
            {busy ? 'Adding…' : !repeating && dates.length > 1 ? `Add ${dates.length} tasks` : 'Add task'}
          </button>
        </>
      }
    >
      <Field label="What needs doing" error={errs.title}>
        <input className="in" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Send revised pricing to Veda" autoFocus />
      </Field>
      <div className="cols c2">
        <Field label="Client" error={errs.client_id}>
          <select
            className="in"
            value={client}
            onChange={(e) => {
              setClient(e.target.value);
              const nc = clients.data?.find((x) => x.id === e.target.value);
              setWho(nc ? nc.spoc.id : me.user.id);
              setErrs((cur) => ({ ...cur, client_id: '' }));
            }}
          >
            <option value="">Internal</option>
            {(clients.data ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={pick && !repeating ? 'SPOC' : 'SPOC (set by client)'} error={errs.assignee_id}>
          {pick && !repeating ? (
            <select className="in" value={who} onChange={(e) => setWho(e.target.value)}>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {c && p.id === c.spoc.id ? ' (client SPOC)' : ''}
                </option>
              ))}
            </select>
          ) : (
            <select className="in" disabled value={defaultWho.id}>
              <option value={defaultWho.id}>{defaultWho.name}</option>
            </select>
          )}
        </Field>
      </div>
      <div className="cols c2">
        {canRepeat ? (
          <Field label="Repeat" error={errs.every}>
            <select
              className="in"
              value={repeat}
              onChange={(e) => {
                setRepeat(e.target.value as Repeat);
                setErrs({});
              }}
            >
              <option value="none">Does not repeat</option>
              {ROUTINE_EVERY.map((k) => (
                <option key={k} value={k}>
                  {ROUTINE_EVERY_LABEL[k]}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label="Urgency" error={errs.priority}>
          <select
            className="in"
            value={prio}
            onChange={(e) => setPrio(e.target.value as TaskPriority)}
            disabled={repeating}
            title={repeating ? 'A task that repeats is planned at the usual urgency' : undefined}
          >
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
            <option value="low">Low</option>
          </select>
        </Field>
        {canRepeat ? null : <div />}
      </div>
      {repeating ? (
        // Prototype ntRep: everyPicker without its "How often" (the Repeat select above is that).
        <div className="cols c2">
          <div>
            {repeat === 'day' ? (
              <Field label="On">
                <input className="in" value="Every working day" disabled />
              </Field>
            ) : repeat === 'month' ? (
              <Field label="On day of the month" error={errs.day_of_month}>
                <input className="in" type="number" min={1} max={28} value={dom}
                  onChange={(e) => {
                    setDom(e.target.value);
                    setErrs((cur) => ({ ...cur, day_of_month: '' }));
                  }}
                />
              </Field>
            ) : (
              <>
                <Field label="On" error={errs.weekday}>
                  <select className="in" value={weekday} onChange={(e) => setWeekday(e.target.value as Weekday)}>
                    {WEEKDAYS.map((d) => (
                      <option key={d}>{d}</option>
                    ))}
                  </select>
                </Field>
                {repeat === 'fortnight' ? (
                  <Field label="Starting" error={errs.start_phase}>
                    <select className="in" value={start} onChange={(e) => setStart(e.target.value as 'this' | 'next')}>
                      <option value="this">This week</option>
                      <option value="next">Next week</option>
                    </select>
                  </Field>
                ) : null}
              </>
            )}
          </div>
          <div />
        </div>
      ) : (
        <>
        <div className="lbl" style={{ marginBottom: 6 }}>
          Due on{' '}
          <span className="sub" style={{ textTransform: 'none', letterSpacing: 0 }}>
            tick one date, or several if the work runs over more than a day
          </span>
        </div>
        <div role="group" aria-label="Due on" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {days.map((d) => {
            const on = dates.includes(d);
            return (
              <label key={d} className={`fp${on ? ' on' : ''}`} style={{ cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center', opacity: isWeekend(d) ? 0.6 : 1 }}>
                <input type="checkbox" checked={on} onChange={() => toggle(d)} aria-label={dayLabel(d)} />
                <span>{shortDay(d, today)}</span>
              </label>
            );
          })}
        </div>
        <div className="split" style={{ marginBottom: 6 }}>
          <button type="button" className="btn sm" onClick={() => quick('week')}>
            Rest of this week
          </button>
          <button type="button" className="btn sm" onClick={() => quick('next3')}>
            Next 3 working days
          </button>
          <button type="button" className="btn sm ghost" onClick={() => quick('none')}>
            Clear
          </button>
        </div>
        {errs.dates || errs.due ? (
          <div className="ferr" role="alert">
            {errs.dates || errs.due}
          </div>
        ) : dates.length > 1 ? (
          <div className="sub" style={{ fontSize: 12.5 }}>
            {dates.length} tasks, one for each date: {dates.map((d) => dayLabel(d)).join(', ')}.
          </div>
        ) : !dates.length ? (
          <div className="sub" style={{ fontSize: 12.5 }}>
            Tick at least one date.
          </div>
        ) : null}
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------- send back (proof check)

export function SendBackModal({ title, by, onClose, onSend, busy }: { title: string; by: string; onClose: Close; onSend: (note: string) => void; busy: boolean }) {
  const [note, setNote] = useState('');
  return (
    <Modal
      title="Send it back"
      sub={
        <>
          {title}
          <br />
          <span className="sub">It reopens for {by} to finish properly.</span>
        </>
      }
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn bad" disabled={busy || !note.trim()} onClick={() => onSend(note.trim())}>
            Send back
          </button>
        </>
      }
    >
      <Field label="What is missing?">
        <textarea className="in" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. The link opens last month’s report" autoFocus />
      </Field>
    </Modal>
  );
}

/** The Act/View segmented control is reused by the acting-as pop-up. */
export { Seg };
